'use client';

import { type ReactNode } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { BadgeSeverity, EntityIcon, Skeleton, Text } from '../../';
import { SignalStatusBadge } from '../signals';
import type {
  SortedEndpointDetectionFinding,
  SortedEndpointDetectionSignal,
} from './utils/endpoint-detections.utils';

type EndpointDetectionsHoverCardProps = {
  findings: SortedEndpointDetectionFinding[];
  signals: SortedEndpointDetectionSignal[];
  isLoading: boolean;
  isError: boolean;
  type: 'finding' | 'signal';
  onFindingSelect?: (findingId: string) => void;
  onSignalSelect?: (signalId: string) => void;
};

const HOVER_CARD_MAX_HEIGHT = '320px';

const DetectionHoverSkeleton = () => (
  <VStack gap={2} alignItems="stretch">
    {Array.from({ length: 4 }).map((_, index) => (
      <HStack key={index} gap={2} alignItems="center" px={1.5} py={1}>
        <Skeleton w="24px" h="24px" borderRadius="sm" flexShrink={0} />
        <Skeleton flex={1} h="20px" borderRadius="sm" />
        <Skeleton w="64px" h="24px" borderRadius="full" flexShrink={0} />
      </HStack>
    ))}
  </VStack>
);

type DetectionHoverRowProps = {
  id: string;
  title: string;
  isActive: boolean;
  entityType: 'finding' | 'signal';
  trailingBadge: ReactNode;
  onSelect?: (id: string) => void;
};

const DetectionHoverRow = ({
  id,
  title,
  isActive,
  entityType,
  trailingBadge,
  onSelect,
}: DetectionHoverRowProps) => (
  <HStack
    gap={2}
    alignItems="center"
    px={1.5}
    py={1}
    borderRadius="sm"
    cursor={onSelect ? 'pointer' : 'default'}
    _hover={onSelect ? { bg: 'bg.subtle' } : undefined}
    onPointerDown={(event) => {
      event.preventDefault();
      event.stopPropagation();
    }}
    onClick={(event) => {
      event.preventDefault();
      event.stopPropagation();
      onSelect?.(id);
    }}
    w="full"
    minW={0}
  >
    <Box
      flexShrink={0}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <EntityIcon
        type={entityType}
        appearance={isActive ? 'highlighted' : 'muted'}
        density="compact"
      />
    </Box>
    <Text
      fontSize="sm"
      color="fg.default"
      overflow="hidden"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
      flex={1}
      minW={0}
    >
      {title}
    </Text>
    {trailingBadge}
  </HStack>
);

export const EndpointDetectionsHoverCard = ({
  findings,
  signals,
  isLoading,
  isError,
  type,
  onFindingSelect,
  onSignalSelect,
}: EndpointDetectionsHoverCardProps) => {
  const tCommon = useTranslations('common');

  if (isLoading) {
    return <DetectionHoverSkeleton />;
  }

  if (isError) {
    return (
      <Box px={1.5} py={2}>
        <Text fontSize="sm" color="fg.error">
          {tCommon('error')}
        </Text>
      </Box>
    );
  }

  const items =
    type === 'finding'
      ? findings.map((finding) => (
          <DetectionHoverRow
            key={finding.id}
            id={finding.id}
            title={finding.title}
            isActive={finding.isActive}
            entityType="finding"
            trailingBadge={
              <BadgeSeverity severity={finding.severity} size="sm">
                {finding.severity}
              </BadgeSeverity>
            }
            onSelect={onFindingSelect}
          />
        ))
      : signals.map((signal) => (
          <DetectionHoverRow
            key={signal.id}
            id={signal.id}
            title={signal.title || '-'}
            isActive={signal.isActive}
            entityType="signal"
            trailingBadge={
              signal.status ? (
                <SignalStatusBadge status={signal.status} />
              ) : undefined
            }
            onSelect={onSignalSelect}
          />
        ));

  return (
    <VStack
      gap={1}
      alignItems="stretch"
      maxH={HOVER_CARD_MAX_HEIGHT}
      overflowY="auto"
    >
      {items}
    </VStack>
  );
};
